import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
  type ListApprovalsResponse,
  type ReviewInternationalWireTransfer,
  type UnifiedTransfer,
  approvalsListApprovals,
  approvalsListApprovalsQueryKey,
  transferCheckReviewTransfer,
  transferRealtimeReviewTransfer,
  transferSwiftReviewInternationalWireTransfer,
} from '@column/api';
import { Dropdown, Icon } from '@column/column-ui-kit';
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Timestamp,
} from '@column/ui';

import { PageActions } from '~/app/Layout';
import { Loading, Pagination } from '~/components';
import { LogoLoading } from '~/elements';
import { useReviewAchTransfer, useReviewWireTransfer } from '~/hooks/useTransfers';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { TransferAmount, TransferTypeBadge } from '~/lib/transfers';
import { getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType, convertTransferAmount } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { PAGE_SIZE_OPTIONS, useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Inner } from '~/styles';
import { TransferReviewDecision } from '~/typings/enum';
import { formatDateTime, getFormattedDate } from '~/util';

import { TransferReviewModal } from './_components/TransferReviewModal';

const extractTransfers = (page: ListApprovalsResponse | undefined): UnifiedTransfer[] =>
  page?.approvals.map((entry) => entry.transfer) ?? [];

export const PageTransferApprovalsOverview: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('approvals');
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [entriesOpen, setEntriesOpen] = useState(false);
  const [reviewCurrentIndex, setReviewCurrentIndex] = useState(0);
  const hasReviewedTransferRef = useRef(false);
  const { cursor, hasPrevious, onNext, onPrevious, pageIndex, setPageSize } = usePaginationState({
    defaultPageSize: pageSize,
  });

  const queryKey = approvalsListApprovalsQueryKey({
    query: { status: 'pending', starting_after: cursor, limit: pageSize },
  });

  const { data, isFetching, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data: response } = await approvalsListApprovals({
        query: { status: 'pending', starting_after: cursor, limit: pageSize },
        throwOnError: true,
      });
      return response;
    },
    placeholderData: keepPreviousData,
  });

  const transfers = useMemo(() => extractTransfers(data), [data]);
  const hasNext = data?.has_more ?? false;
  const totalResultsCount = data?.total_results_count ?? 0;
  const tableMinHeight = transfers.length === 0 ? 341 : undefined;

  const { createRequest: createAchReviewReq } = useReviewAchTransfer({
    onError: (err) => {
      addDangerNotification({
        content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
      });
    },
  });
  const { createRequest: createWireReviewReq } = useReviewWireTransfer({
    onError: (err) => {
      addDangerNotification({
        content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
      });
    },
  });
  const reviewTransfer = useCallback(
    async (id: string, transferType: TransferEntryType, req: ReviewInternationalWireTransfer) => {
      if (transferType === 'ach_credit' || transferType === 'ach_debit') {
        await createAchReviewReq({
          id,
          comment: req.comment,
          decision: req.decision as TransferReviewDecision,
        });
      } else if (transferType === 'check_debit') {
        try {
          await transferCheckReviewTransfer({
            path: { check_transfer_id: id },
            body: { comment: req.comment, decision: req.decision },
            throwOnError: true,
          });
        } catch (err) {
          addDangerNotification({
            content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
          });
          throw err;
        }
      } else if (transferType === 'realtime') {
        try {
          await transferRealtimeReviewTransfer({
            path: { realtime_transfer_id: id },
            body: { comment: req.comment, decision: req.decision },
            throwOnError: true,
          });
        } catch (err) {
          addDangerNotification({
            content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
          });
          throw err;
        }
      } else if (transferType === 'swift') {
        try {
          await transferSwiftReviewInternationalWireTransfer({
            path: { swift_transfer_id: id },
            body: req,
            throwOnError: true,
          });
        } catch (err) {
          if ((err as { code?: string })?.code !== 'fx_quote_expired') {
            addDangerNotification({
              content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
            });
          }
          throw err;
        }
      } else {
        await createWireReviewReq({
          id,
          comment: req.comment,
          decision: req.decision as TransferReviewDecision,
        });
      }
    },
    [addDangerNotification, createAchReviewReq, createWireReviewReq]
  );

  const handleTimeOptionChange = useCallback(
    (value: TableTimeFormat | undefined) => {
      if (!value) return;
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [updateTableConfig, addDangerNotification]
  );

  const columns: DataTableColumnDef<UnifiedTransfer>[] = useMemo(
    () => [
      {
        header: 'Amount',
        accessor: 'amount',
        width: 'minmax(300px, auto)',
        className: 'min-w-0',
        cell: ({ row, value }) => (
          <TransferAmount
            amount={Math.abs(Number(value ?? 0))}
            currencyCode={row.currency_code}
            description={row.description}
            isAdding={convertTransferAmount(row.type as TransferEntryType, row.is_incoming) > 0}
          />
        ),
      },
      {
        header: 'Type',
        accessor: 'type',
        width: 'minmax(min-content, 150px)',
        cell: ({ value }) => <TransferTypeBadge type={value as TransferEntryType} />,
      },
      {
        header: (
          <Dropdown
            size="small"
            positionRight
            active={timeFormat}
            options={[
              { label: 'Relative Time', value: TableTimeFormat.Relative },
              { label: 'Date / Time', value: TableTimeFormat.Absolute },
            ]}
            onChange={handleTimeOptionChange}
            customLabel={
              <Flex alignItems="center" gap={4}>
                Created <Icon.ChevronDown />
              </Flex>
            }
          />
        ),
        accessor: 'created_at',
        width: 'min-content',
        cell: ({ value }) => (
          <Timestamp
            date={value}
            align="right"
            className="text-xs text-muted-foreground"
            formatHover={(hoverDate) =>
              formatDateTime(hoverDate, { timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc' })
            }
          >
            {timeFormat === TableTimeFormat.Relative
              ? getFormattedDate(value, 'time-passed')
              : formatDateTime(value, { timeZoneFormat: 'local' })}
          </Timestamp>
        ),
      },
      {
        header: '',
        accessor: 'id',
        width: 'minmax(150px, max-content)',
        className: 'justify-end',
        cell: ({ row }) => (
          <Button
            onClick={(event: React.SyntheticEvent) => {
              event.stopPropagation();
              navigate(getTransferURL(row.id, row.type as TransferEntryType));
            }}
            size="xs"
            variant="secondary"
            type="button"
          >
            View Transfer
            <Icon.ChevronRightLarge style={{ '--icon-size': '14px' } as React.CSSProperties} />
          </Button>
        ),
      },
    ],
    [handleTimeOptionChange, navigate, timeFormat]
  );

  const transferStartIndex = pageIndex * pageSize;

  const openReviewModal = useCallback((startIndex: number) => {
    hasReviewedTransferRef.current = false;
    setReviewCurrentIndex(startIndex);
    setIsApprovalModalOpen(true);
  }, []);

  const onRowClick = useCallback(
    (row: UnifiedTransfer) => {
      const idx = transfers.findIndex((transfer) => transfer.id === row.id);
      if (idx < 0) return;
      openReviewModal(transferStartIndex + idx);
    },
    [openReviewModal, transferStartIndex, transfers]
  );

  const handleModalClose = useCallback(() => {
    setIsApprovalModalOpen(false);
    if (!hasReviewedTransferRef.current) return;
    hasReviewedTransferRef.current = false;

    const listQueryId = approvalsListApprovalsQueryKey()[0]._id;
    queryClient.invalidateQueries({
      predicate: ({ queryKey: key }) => (key[0] as { _id?: string } | undefined)?._id === listQueryId,
    });
  }, [queryClient]);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPageSize(value);
      updateTableConfig({ limit: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [setPageSize, updateTableConfig, addDangerNotification]
  );

  return (
    <>
      <TransferReviewModal
        transfers={transfers}
        transferStartIndex={transferStartIndex}
        currentIndex={reviewCurrentIndex}
        totalTransfers={totalResultsCount}
        isOpen={isApprovalModalOpen}
        onClose={handleModalClose}
        onSubmit={async (transferId, transferType, request) => {
          await reviewTransfer(transferId, transferType, request);
          hasReviewedTransferRef.current = true;
          queryClient.setQueryData<ListApprovalsResponse>(queryKey, (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              approvals: oldData.approvals.filter((entry) => entry.transfer.id !== transferId),
            };
          });
        }}
      />
      {transfers.length > 0 && (
        <PageActions>
          <Button onClick={() => openReviewModal(transferStartIndex)} size="sm" variant="primary">
            Review Transfers
          </Button>
        </PageActions>
      )}
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<UnifiedTransfer>
            columns={columns}
            data={transfers}
            isLoading={isFetching}
            loadingContent={<LogoLoading />}
            style={{ minHeight: tableMinHeight }}
            emptyContent={
              <Empty className="min-h-[300px]">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon.AnimationArrows />
                  </EmptyMedia>
                  <EmptyTitle>No transfers found</EmptyTitle>
                  <EmptyDescription>No transfers are awaiting approval right now.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            }
            rowKey={(row) => row.id}
            rowClassName="min-h-11"
            onRowClick={onRowClick}
          />

          <div className="relative z-[1] flex items-center justify-between pt-4">
            <Dropdown
              options={PAGE_SIZE_OPTIONS.map((entries: number) => ({
                label: entries.toString(),
                value: entries,
              }))}
              size="small"
              variant="muted"
              active={pageSize}
              customLabel={
                <Badge
                  variant="secondary"
                  className="rounded-[8px] [&>svg]:ml-[-4px] [&>svg]:inline-block [&>svg]:align-top"
                >
                  {pageSize}
                  {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                </Badge>
              }
              onOpenChange={setEntriesOpen}
              onChange={handlePageSizeChange}
            />
            <Pagination
              current={pageIndex + 1}
              disablePrev={!hasPrevious}
              disableNext={!hasNext}
              onPrev={onPrevious}
              onNext={() => {
                if (transfers.length > 0) onNext({ cursor: transfers[transfers.length - 1].id });
              }}
            />
          </div>
        </Inner>
      </Loading>
    </>
  );
};
